import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { getProducts, priceForQuantity, createOrder } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import MockupPreview from "@/components/MockupPreview";
import Customizer from "@/components/Customizer";
import Footer from "@/components/Footer";
import { Minus, Plus, Trash2, ArrowLeft, ArrowRight, PartyPopper } from "lucide-react";

const STEPS = ["Sélection", "Personnalisation", "Coordonnées"];

export default function Quote() {
  const { items, setQuantity, removeItem, clear, customization, updateSide } = useCart();
  const [products, setProducts] = useState({});
  const [step, setStep] = useState(0);
  const [custSide, setCustSide] = useState("front");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    getProducts().then((list) => setProducts(Object.fromEntries(list.map((p) => [p.slug, p])))).catch(() => {});
  }, []);

  const colorHex = (product, name) => product?.colors?.find((cc) => cc.name === name)?.hex || null;

  const lines = items
    .map((it) => {
      const p = products[it.slug];
      if (!p) return null;
      const unit = priceForQuantity(p.tiers, it.quantity);
      return { ...it, product: p, unit, total: unit * it.quantity, hex: colorHex(p, it.color) };
    })
    .filter(Boolean);
  const grandTotal = lines.reduce((s, l) => s + l.total, 0);

  const buildCustom = () => {
    const out = {};
    for (const sd of ["front", "back"]) {
      const s = customization[sd];
      out[sd] = { logo_file_id: s.logo?.id || null, logo_placement: s.logoPlacement, text: s.text, text_placement: s.textPlacement, text_color: s.textColor };
    }
    return out;
  };

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Nom et email requis");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createOrder({
        customer: { name: form.name, company: form.company, email: form.email, phone: form.phone },
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity, color: i.color })),
        customization: buildCustom(),
        message: form.message,
      });
      setDone(res);
      clear();
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-lg">
            <PartyPopper className="w-14 h-14 mx-auto text-[#0044ff]" />
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl mt-6">Demande envoyée</h1>
            <p className="text-[#555] text-lg mt-4">Merci ! Votre demande de devis (réf. {done.id.slice(0, 8)}) est bien reçue. Nous revenons vers vous sous 24h.</p>
            <Link to="/" data-testid="back-home" className="inline-block mt-10 bg-black text-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0044ff] transition-colors">Retour à l'accueil</Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const renderPreview = (l, sd) => (
    <div key={l.key + sd}>
      <MockupPreview
        image={(sd === "front" ? l.product.image_front : l.product.image_back) || l.product.image}
        colorHex={l.hex}
        colorName={l.color}
        logoUrl={customization[sd].logo?.url}
        logoPlacement={customization[sd].logoPlacement}
        onLogoPlacement={(pl) => updateSide(sd, { logoPlacement: pl })}
        text={customization[sd].text}
        textPlacement={customization[sd].textPlacement}
        textColor={customization[sd].textColor}
        onTextPlacement={(pl) => updateSide(sd, { textPlacement: pl })}
        className="w-full aspect-[4/5] border border-[#e5e5e5]"
      />
      <p className="text-center font-mono text-[10px] uppercase tracking-widest text-[#999] mt-1">{sd === "front" ? "Devant" : "Dos"}</p>
    </div>
  );

  return (
    <div className="grain min-h-screen flex flex-col">
      <section className="max-w-[1100px] w-full mx-auto px-6 md:px-12 pt-32 pb-24 flex-1">
        <div className="flex items-center justify-between mb-10">
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">Votre devis</h1>
          <Link to="/#catalogue" className="font-mono text-xs uppercase tracking-widest text-[#555] hover:text-black">+ Ajouter des produits</Link>
        </div>

        <div className="flex items-center gap-4 mb-14 flex-wrap">
          {STEPS.map((sName, i) => (
            <div key={sName} className="flex items-center gap-3">
              <div className={`w-8 h-8 flex items-center justify-center font-mono text-sm border ${i <= step ? "bg-[#0044ff] text-white border-[#0044ff]" : "border-[#ccc] text-[#999]"}`}>{i + 1}</div>
              <span className={`text-xs font-bold uppercase tracking-widest ${i <= step ? "text-black" : "text-[#999]"}`}>{sName}</span>
              {i < STEPS.length - 1 && <span className="w-8 h-px bg-[#ccc]" />}
            </div>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="border border-dashed border-[#ccc] py-24 text-center">
            <p className="text-[#555] text-lg">Votre devis est vide.</p>
            <Link to="/#catalogue" className="inline-block mt-6 bg-black text-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0044ff] transition-colors">Parcourir le catalogue</Link>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="space-y-4">
                  {lines.map((l) => (
                    <div key={l.key} data-testid={`cart-line-${l.slug}`} className="flex items-center gap-4 md:gap-6 border border-[#e5e5e5] bg-white p-4">
                      <img src={l.product.image} alt={l.product.name} className="w-20 h-24 object-cover bg-[#ebebea]" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-xl">{l.product.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {l.color && <span className="w-3.5 h-3.5 rounded-full border border-[#ccc]" style={{ background: l.hex }} />}
                          <p className="font-mono text-xs text-[#555]">{l.color ? `${l.color} · ` : ""}{l.unit}€ / pièce</p>
                        </div>
                      </div>
                      <div className="flex items-center border border-black">
                        <button onClick={() => setQuantity(l.key, l.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-black hover:text-white"><Minus className="w-3 h-3" /></button>
                        <input type="number" value={l.quantity} onChange={(e) => setQuantity(l.key, parseInt(e.target.value) || 1)} className="w-14 h-9 text-center font-bold outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                        <button onClick={() => setQuantity(l.key, l.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-black hover:text-white"><Plus className="w-3 h-3" /></button>
                      </div>
                      <p className="font-display font-bold text-xl w-24 text-right">{l.total.toLocaleString("fr-FR")}€</p>
                      <button onClick={() => removeItem(l.key)} data-testid={`remove-${l.slug}`} className="text-[#999] hover:text-red-500"><Trash2 className="w-5 h-5" /></button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="grid lg:grid-cols-2 gap-10">
                <div>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {[{ k: "front", l: "Devant" }, { k: "back", l: "Dos" }].map((sd) => (
                      <button key={sd.k} data-testid={`cust-tab-${sd.k}`} onClick={() => setCustSide(sd.k)} className={`py-3 font-bold uppercase tracking-widest text-xs border transition-colors ${custSide === sd.k ? "bg-black text-white border-black" : "bg-white border-[#ccc] hover:border-black"}`}>{sd.l}</button>
                    ))}
                  </div>
                  <div className="border border-[#e5e5e5] bg-[#f9f9f8] p-5">
                    <Customizer side={custSide} />
                  </div>
                  <p className="text-xs text-[#555] mt-4">Ajoutez un logo et/ou un texte, différents pour le devant et le dos. Glissez-les directement sur l'aperçu.</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#555] mb-3">Aperçu réaliste</p>
                  <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
                    {lines.map((l) => (
                      <div key={l.key}>
                        <p className="font-mono text-[11px] uppercase tracking-widest text-[#555] mb-2">{l.product.name}{l.color ? ` · ${l.color}` : ""}</p>
                        <div className="grid grid-cols-2 gap-4">
                          {renderPreview(l, "front")}
                          {renderPreview(l, "back")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="grid md:grid-cols-2 gap-4">
                {[
                  { k: "name", l: "Nom complet *", ph: "Jean Dupont" },
                  { k: "company", l: "Nom de l'entreprise", ph: "Votre société" },
                  { k: "email", l: "Email *", ph: "jean@societe.com", type: "email" },
                  { k: "phone", l: "Téléphone", ph: "06 12 34 56 78" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="font-mono text-xs uppercase tracking-widest text-[#555] block mb-2">{f.l}</label>
                    <input type={f.type || "text"} data-testid={`field-${f.k}`} value={form[f.k]} placeholder={f.ph} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="w-full border border-[#ccc] bg-white px-4 py-3 outline-none focus:border-black transition-colors" />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="font-mono text-xs uppercase tracking-widest text-[#555] block mb-2">Votre projet</label>
                  <textarea data-testid="field-message" value={form.message} placeholder="Précisez emplacement du marquage, délai souhaité…" onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="w-full border border-[#ccc] bg-white px-4 py-3 outline-none focus:border-black transition-colors resize-none" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {items.length > 0 && (
          <div className="flex items-center justify-between mt-14 pt-8 border-t border-[#e5e5e5]">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-[#555]">Total estimé</p>
              <p data-testid="quote-total" className="font-display font-black text-4xl tracking-tighter">{grandTotal.toLocaleString("fr-FR")}€</p>
            </div>
            <div className="flex items-center gap-3">
              {step > 0 && (
                <button onClick={() => setStep((s) => s - 1)} data-testid="step-back" className="flex items-center gap-2 px-6 py-4 border border-black font-bold uppercase tracking-widest text-sm hover:bg-black hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> Retour</button>
              )}
              {step < 2 ? (
                <button onClick={() => setStep((s) => s + 1)} data-testid="step-next" className="flex items-center gap-2 bg-black text-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0044ff] transition-colors">Continuer <ArrowRight className="w-4 h-4" /></button>
              ) : (
                <button onClick={submit} disabled={submitting} data-testid="submit-quote" className="flex items-center gap-2 bg-[#0044ff] text-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0033cc] transition-colors disabled:opacity-50">{submitting ? "Envoi…" : "Envoyer ma demande"} <ArrowRight className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
